import { stockMovementValue } from "../../../../utility/stockValuation";
import "./StockModal.scss";
import FormField from "../../../../components/formField/FormField";
import { useEffect, useMemo, useState } from "react";
import { useAlert } from "react-alert";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import * as yup from "yup";
import StockModal from "./StockModal";
import { collection, doc, updateDoc, writeBatch } from "firebase/firestore";
import { db } from "../../../../firebase";
import { isDecimalInput } from "../../../../utility/inputValidation";

const DEFAULT_FORM_DATA = {
  name: "",
  categoryId: null,
  unit: "",
  minAlert: "",
  startingStock: "",
  unitCost: "",
};
export default function CreateStockModal({
  open,
  onClose,
  onCreated,
  categories,
  venueId,
  stockItem,
}) {
  const { t } = useTranslation("stockManagement");
  const alert = useAlert();
  const userStore = useSelector((state) => state.userReducer);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!stockItem?.id;
  const stockItemValidationSchema = useMemo(
    () =>
      yup.object({
        name: yup.string().trim().required(t("verification_stock_item_name_required")),
        categoryId: yup.string().trim().required(t("verification_stock_item_category_required")),
        unit: yup.string().trim().required(t("verification_stock_item_unit_required")),
        minAlert: yup
          .number()
          .typeError(t("verification_stock_item_min_alert_number"))
          .required(t("verification_stock_item_min_alert_required"))
          .min(0, t("verification_stock_item_min_alert_min")),
        unitCost: yup.number().nullable().typeError(t("stock_unit_cost_invalid")).min(0, t("stock_unit_cost_invalid")).test("finite", t("stock_unit_cost_invalid"), value => value == null || Number.isFinite(value)),
        startingStock: yup
          .number()
          .typeError(t("verification_stock_item_starting_stock_number"))
          .min(0, t("verification_stock_item_starting_stock_min")),
      }),
    [t],
  );

  useEffect(() => {
    if (open && isEditing) {
      setFormData({
        name: stockItem?.name || "",
        categoryId: stockItem?.categoryId || "",
        unit: stockItem?.unit || "",
        minAlert:
          stockItem?.minAlert || stockItem?.minAlert === 0 ? String(stockItem.minAlert) : "",
        unitCost: stockItem?.unitCost == null ? "" : String(stockItem.unitCost),
        startingStock: "",
      });
    } else if (open) {
      setFormData(DEFAULT_FORM_DATA);
    }
  }, [open, isEditing, stockItem]);

  const handleConfirm = async () => {
    try {
      setIsSubmitting(true);
      setErrors({});

      const payload = {
        name: formData.name.trim(),
        categoryId: formData.categoryId,
        unit: formData.unit.trim(),
        minAlert: formData.minAlert === "" ? null : Number(formData.minAlert),
        startingStock: formData.startingStock === "" ? 0 : Number(formData.startingStock),
        unitCost: formData.unitCost === "" ? null : Number(formData.unitCost),
        updatedAt: Date.now(),
      };

      await stockItemValidationSchema.validate(payload, { abortEarly: false });

      if (isEditing) {
        const updatePayload = { ...payload };
        delete updatePayload.startingStock;
        await updateDoc(doc(db, "StockItems", stockItem.id), updatePayload);
      } else {
        const batch = writeBatch(db);
        const stockItemRef = doc(collection(db, "StockItems"));
        const startingStock = payload.startingStock || 0;
        const createPayload = { ...payload };
        delete createPayload.startingStock;
        batch.set(stockItemRef, {
          ...createPayload,
          onHand: startingStock,
          totalPrice: 0,
          venueId,
        });
        if (startingStock > 0) {
          const movementRef = doc(collection(db, "StockMovements"));
          batch.set(movementRef, {
            venueId,
            stockItemId: stockItemRef.id,
            delta: startingStock,
            reason: "initial_stock",
            ...stockMovementValue(payload, startingStock),
            createdBy: userStore?.users?.uid || null,
            createdAt: Date.now(),
          });
        }
        await batch.commit();
      }

      setFormData(DEFAULT_FORM_DATA);
      await onCreated();
    } catch (err) {
      if (err?.name === "ValidationError") {
        const formattedErrors = {};
        err.inner.forEach((error) => (formattedErrors[error.path] = error.message));
        setErrors(formattedErrors);
      } else {
        alert.show(
          t(isEditing ? "edit_stock_modal_submit_error" : "create_stock_modal_submit_error"),
          { type: "error" },
        );
        console.error(`Error ${isEditing ? "updating" : "creating"} stock item:`, err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateHandler = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const categoriesFormatted = useMemo(
    () => categories.map((category) => ({ value: category.id, label: category.name })),
    [categories],
  );

  const closeModal = () => {
    if (isSubmitting) return;
    setFormData(DEFAULT_FORM_DATA);
    setErrors({});
    onClose();
  };

  return (
    <StockModal
      open={open}
      onClose={closeModal}
      onConfirm={handleConfirm}
      isLoading={isSubmitting}
      title={isEditing ? t("stock_overview_action_edit") : t("create_stock_modal_title")}
    >
      <div className="stock-action-modal-products">
        <p className="stock-action-modal-products-subtitle">
          {t(isEditing ? "edit_stock_modal_subtitle" : "create_stock_modal_subtitle")}
        </p>

        <FormField
          label={t("create_stock_modal_name")}
          value={formData.name}
          error={errors.name}
          onChange={(value) => updateHandler("name", value)}
        />
        <FormField
          label={t("create_stock_modal_unit")}
          value={formData.unit}
          error={errors.unit}
          onChange={(value) => updateHandler("unit", value)}
        />
        <FormField
          label={t("create_stock_modal_min_alert")}
          value={formData.minAlert}
          error={errors.minAlert}
          onChange={(value) => {
            if (isDecimalInput(value)) updateHandler("minAlert", value);
          }}
        />
        <FormField label={t("stock_unit_cost")} value={formData.unitCost} error={errors.unitCost}
          onChange={value => { if (isDecimalInput(value)) updateHandler("unitCost", value); }} />
        {!isEditing && (
          <FormField
            label={t("create_stock_modal_starting_stock")}
            value={formData.startingStock}
            error={errors.startingStock}
            onChange={(value) => {
              if (isDecimalInput(value)) updateHandler("startingStock", value);
            }}
          />
        )}
        <FormField
          type="dropdown"
          label={t("create_stock_modal_category")}
          value={formData.categoryId}
          error={errors.categoryId}
          dropDownValues={categoriesFormatted}
          onChange={(value) => updateHandler("categoryId", value)}
        />
      </div>
    </StockModal>
  );
}
